import type { GroupStudentResponse } from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import { Card, CardHeader, CardTitle } from '@lms/ui/card';
import {
  isLastMonth,
  isNever,
  isNotThisYear,
  isThisMonth,
  isThisYear,
} from '@lms/ui/date-presets';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@lms/ui/dropdown-menu';
import { SortableTableHead } from '@lms/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lms/ui/table';
import { TableFilter } from '@lms/ui/table-filter';
import {
  type FilterConfig,
  type SortConfig,
  useTableView,
} from '@lms/ui/use-table-view';
import {
  ChevronDown,
  CircleCheckIcon,
  CirclePlayIcon,
  DownloadIcon,
  TimerIcon,
  XIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLocale, useTranslations } from 'use-intl';
import { CardTableContent } from '@/components/admin/card-table';
import { useBrand } from '@/features/branding/brand-provider';
import EllipsisSpan from '@/features/groups/components/ellipsis-span';
import IconTooltip from '@/features/groups/components/icon-tooltip';
import OverflowMenu from '@/features/groups/components/studentlist_overflowmenu';
import { takeLocaleString } from '@/lib/translate';
import { useUrlTableState } from '@/lib/use-url-table-state';
import { downloadXlsx, toFileSlug } from '@/lib/xlsx';

type SessionTableProps = {
  students: GroupStudentResponse[];
  curriculums: [string, string][];
  courses: string[];
  groupName: string;
  /** Whether the viewer may remove students (shows the row overflow menu). */
  canManage: boolean;
  onChanged: () => void;
};

function passedCounts(student: GroupStudentResponse): {
  passed: number;
  total: number;
} {
  const values = Object.values(student.courseStatus ?? {});
  return {
    passed: values.filter((v) => v === 'passed').length,
    total: values.length,
  };
}

export default function SessionTable({
  students,
  curriculums,
  courses,
  groupName,
  canManage,
  onChanged,
}: SessionTableProps) {
  const t = useTranslations('admin');
  const t_status = useTranslations('me.course_status');
  const locale = useLocale();
  const { slug, brand } = useBrand();

  const curriculumLabel = (student: GroupStudentResponse) =>
    takeLocaleString(
      locale,
      student.curriculumNameTranslated ?? student.curriculumName,
    ) ??
    student.curriculumName ??
    '';

  // Curriculum is a genuine multi-value filter (not a named preset), so it
  // stays its own local multi-select rather than living in useTableView.
  const [curriculumFilter, setCurriculumFilter] = useState<string[]>(
    curriculums.map((c) => c[0]),
  );
  const allCurriculums = curriculumFilter.length === curriculums.length;

  const toggleCurriculum = (curriculum: string) => {
    if (curriculumFilter.includes(curriculum)) {
      setCurriculumFilter(curriculumFilter.filter((c) => c !== curriculum));
    } else {
      setCurriculumFilter([...curriculumFilter, curriculum]);
    }
  };

  const byCurriculum = students.filter((student) =>
    curriculumFilter.includes(student.curriculumName ?? ''),
  );

  const sorts = useMemo<Record<string, SortConfig<GroupStudentResponse>>>(
    () => ({
      id: { accessor: (s) => s.id, type: 'number', firstDir: 'asc' },
      code: { accessor: (s) => s.code ?? null, type: 'string' },
      lastActive: { accessor: (s) => s.lastActive ?? null, type: 'date' },
      curriculum: {
        accessor: (s) =>
          takeLocaleString(
            locale,
            s.curriculumNameTranslated ?? s.curriculumName,
          ) ??
          s.curriculumName ??
          '',
        type: 'string',
      },
    }),
    [locale],
  );
  const filters = useMemo<Record<string, FilterConfig<GroupStudentResponse>>>(
    () => ({
      active: {
        default: 'any',
        options: {
          any: () => true,
          this_month: (s) => isThisMonth(s.lastActive),
          last_month: (s) => isLastMonth(s.lastActive),
          this_year: (s) => isThisYear(s.lastActive),
          not_this_year: (s) =>
            isNever(s.lastActive) || isNotThisYear(s.lastActive),
        },
      },
      passed: {
        default: 'any',
        options: {
          any: () => true,
          none: (s) => passedCounts(s).passed === 0,
          some: (s) => passedCounts(s).passed > 0,
          all: (s) => {
            const { passed, total } = passedCounts(s);
            return total > 0 && passed === total;
          },
        },
      },
    }),
    [],
  );
  const urlConfig = useMemo(
    () => ({
      sortKeys: ['id', 'code', 'lastActive', 'curriculum'],
      defaultSort: { key: 'lastActive', dir: 'desc' as const },
      filters: {
        active: {
          options: [
            'any',
            'this_month',
            'last_month',
            'this_year',
            'not_this_year',
          ],
          default: 'any',
        },
        passed: { options: ['any', 'none', 'some', 'all'], default: 'any' },
      },
    }),
    [],
  );
  const { value, onChange } = useUrlTableState(urlConfig);
  const view = useTableView(byCurriculum, { sorts, filters, value, onChange });

  const hasQuery = view.isFiltered || !allCurriculums;
  const clearAll = () => {
    view.reset();
    setCurriculumFilter(curriculums.map((c) => c[0]));
  };
  const columnCount = 4 + courses.length + (canManage ? 1 : 0);

  const formatLastActive = (value: string) =>
    new Date(value).toLocaleString(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });

  // Exports exactly what the table shows: filtered, sorted, same columns.
  const handleExport = () => {
    const rows = [
      [t('id'), t('code'), t('last_active'), t('curriculum'), ...courses],
      ...view.rows.map((student) => [
        student.id,
        student.code ?? '',
        formatLastActive(student.lastActive),
        curriculumLabel(student),
        ...courses.map((course) => {
          const status = student.courseStatus?.[course];
          return status === 'not_started' ||
            status === 'ongoing' ||
            status === 'passed'
            ? t_status(status)
            : '';
        }),
      ]),
    ];
    const date = new Date().toISOString().slice(0, 10);
    void downloadXlsx(
      `${slug}-${toFileSlug(groupName)}-${date}.xlsx`,
      groupName,
      rows,
      brand.primaryColor,
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row flex-wrap items-center gap-3">
        {curriculums.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={
                  allCurriculums ? undefined : 'border-primary text-primary'
                }
              >
                <span className="text-muted-foreground">
                  {t('curriculum')}:
                </span>
                <span className="font-medium">
                  {allCurriculums
                    ? t('all')
                    : `${curriculumFilter.length}/${curriculums.length}`}
                </span>
                <ChevronDown className="size-4 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuCheckboxItem
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={(checked: boolean) =>
                  setCurriculumFilter(
                    checked ? curriculums.map((c) => c[0]) : [],
                  )
                }
                checked={allCurriculums}
              >
                {t('all')}
              </DropdownMenuCheckboxItem>
              {curriculums.map(([name, translated]) => (
                <DropdownMenuCheckboxItem
                  key={name}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={() => toggleCurriculum(name)}
                  checked={curriculumFilter.includes(name)}
                >
                  {translated}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <TableFilter
          label={t('last_active')}
          value={view.filterValues.active}
          defaultValue="any"
          onChange={(v) => view.setFilter('active', v)}
          options={[
            { value: 'any', label: t('filter_any') },
            { value: 'this_month', label: t('filter_this_month') },
            { value: 'last_month', label: t('filter_last_month') },
            { value: 'this_year', label: t('filter_this_year') },
            { value: 'not_this_year', label: t('filter_not_this_year') },
          ]}
        />
        <TableFilter
          label={t('passed_courses')}
          value={view.filterValues.passed}
          defaultValue="any"
          onChange={(v) => view.setFilter('passed', v)}
          options={[
            { value: 'any', label: t('all') },
            { value: 'none', label: t('filter_none_passed') },
            { value: 'some', label: t('filter_at_least_one_passed') },
            { value: 'all', label: t('filter_all_passed') },
          ]}
        />
        {hasQuery && (
          <Button variant="ghost" size="sm" onClick={clearAll}>
            <XIcon className="mr-1 h-4 w-4" />
            {t('clear_filters')}
          </Button>
        )}
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <CardTitle>{t('sessions')}</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={view.rows.length === 0}
          >
            <DownloadIcon className="mr-2 h-4 w-4" />
            {t('export_xlsx')}
          </Button>
        </CardHeader>
        <CardTableContent>
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  sortKey="id"
                  direction={view.sortDirFor('id')}
                  onSort={view.toggleSort}
                >
                  {t('id')}
                </SortableTableHead>
                <SortableTableHead
                  sortKey="code"
                  direction={view.sortDirFor('code')}
                  onSort={view.toggleSort}
                >
                  {t('code')}
                </SortableTableHead>
                <SortableTableHead
                  sortKey="lastActive"
                  direction={view.sortDirFor('lastActive')}
                  onSort={view.toggleSort}
                >
                  {t('last_active')}
                </SortableTableHead>
                <SortableTableHead
                  sortKey="curriculum"
                  direction={view.sortDirFor('curriculum')}
                  onSort={view.toggleSort}
                >
                  {t('curriculum')}
                </SortableTableHead>
                {courses.map((name) => (
                  <TableHead key={name}>
                    <EllipsisSpan className="align-middle">{name}</EllipsisSpan>
                  </TableHead>
                ))}
                {canManage && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {view.rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    className="text-muted-foreground py-8 text-center"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <span>{t('no_matches')}</span>
                      {hasQuery && (
                        <Button variant="ghost" size="sm" onClick={clearAll}>
                          <XIcon className="mr-1 h-4 w-4" />
                          {t('clear_filters')}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                view.rows.map((student) => (
                  <TableRow key={student.id}>
                    <TableCell>{student.id}</TableCell>
                    <TableCell>{student.code ?? '—'}</TableCell>
                    <TableCell className="min-w-48">
                      {formatLastActive(student.lastActive)}
                    </TableCell>
                    <TableCell>
                      <EllipsisSpan className="min-w-40">
                        {curriculumLabel(student) || '—'}
                      </EllipsisSpan>
                    </TableCell>
                    {courses.map((course) => {
                      const status = student.courseStatus?.[course] ?? '-';
                      return (
                        <TableCell key={course} className="text-center">
                          {status === 'not_started' && (
                            <IconTooltip tooltip={t_status('not_started')}>
                              <CirclePlayIcon className="text-gray-600" />
                            </IconTooltip>
                          )}
                          {status === 'ongoing' && (
                            <IconTooltip tooltip={t_status('ongoing')}>
                              <TimerIcon className="text-yellow-600" />
                            </IconTooltip>
                          )}
                          {status === 'passed' && (
                            <IconTooltip tooltip={t_status('passed')}>
                              <CircleCheckIcon className="text-green-600" />
                            </IconTooltip>
                          )}
                          {status === '-' && '-'}
                        </TableCell>
                      );
                    })}
                    {canManage && (
                      <TableCell className="text-right">
                        <OverflowMenu id={student.id} onRemoved={onChanged} />
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardTableContent>
      </Card>
    </div>
  );
}
